import { useSolarDesignerInverters } from '@/api/queries/project/useSolarDesignerInverters';
import type { ProjectSolarPotential } from '@/api/queries/project/useGetProjectSolarPotential';
import type {
  SolarDesignDraftFieldsFragment,
  SolarRecommendedApplianceFieldsFragment,
} from '@/gql/graphql';
import { solarPanelAppliancesForLayout } from '@/ui/pages/solarDesignerPage/solarDesigner/solarEquipment';
import { H4, SkeletonLoader, Text } from '@payaca/ambrosia';
import {
  selectedSolarInverterApplianceId,
  solarDesignDraftStateSchema,
  type SolarDesignDraftState,
} from '@payaca/helpers/solarDesignDraft';
import { FC, useMemo } from 'react';
import { resolveQuotedSolarModules } from '@payaca/helpers/solarQuotedModules';
import SuggestedEquipment, {
  type SuggestedEquipmentItem,
} from './SuggestedEquipment';

type SolarDesignEquipmentProps = {
  projectId: string;
  source: ProjectSolarPotential;
  draft: SolarDesignDraftFieldsFragment;
};

type EquipmentContext = {
  state: SolarDesignDraftState;
  panelsCount: number;
  panelCapacityWatts: number | null;
  panelAppliances: SolarRecommendedApplianceFieldsFragment[];
  legacyBatterySelection: boolean;
};

const solarDesignEquipmentContext = (
  source: ProjectSolarPotential,
  draftState: unknown
): EquipmentContext | null => {
  // Read-only compatibility for pre-selection drafts. Absence is a
  // recommendation; an explicit saved null is an exclusion.
  const raw = draftState && typeof draftState === 'object' ? draftState : {};
  const legacyBatterySelection = !Object.hasOwn(
    raw,
    'selectedBatteryApplianceId'
  );
  const parsed = solarDesignDraftStateSchema.safeParse({
    selectedPanelApplianceId: null,
    selectedBatteryApplianceId: null,
    ...raw,
  });
  if (!parsed.success) return null;

  const state = parsed.data;
  const geometryMode =
    !!source.geometry?.packedPanels || state.layout.manualMode;
  const excludedFaces = new Set(state.layout.excludedFaceIds);
  const panelsCount = geometryMode
    ? state.layout.workingPanels.filter(
        (panel) => panel.included && !excludedFaces.has(panel.faceId)
      ).length
    : state.selectedPanelIndexes.length;

  const panelAppliances = solarPanelAppliancesForLayout(
    source.recommendedPanelAppliances,
    source.geometry,
    geometryMode
  );
  const chosenPanel = state.selectedPanelApplianceId
    ? panelAppliances.find(
        ({ appliance }) => appliance.id === state.selectedPanelApplianceId
      )
    : panelAppliances[0];
  const sourceWatts =
    source.geometry?.panelDimensions?.capacityWatts ??
    source.panelCapacityWatts ??
    chosenPanel?.appliance.productSpec.panelCapacityW ??
    null;
  const quotedModules = resolveQuotedSolarModules(
    {
      panelsCount,
      panelCapacityWatts: sourceWatts ?? 0,
      preservePanelCount: geometryMode,
    },
    chosenPanel?.appliance.productSpec.panelCapacityW
      ? { panelCapacityW: chosenPanel.appliance.productSpec.panelCapacityW }
      : null
  );
  return {
    state,
    panelsCount: quotedModules.panelsCount,
    panelCapacityWatts: quotedModules.panelCapacityWatts || null,
    panelAppliances,
    legacyBatterySelection,
  };
};

const resolvedEquipmentItem = (
  label: string,
  appliances: SolarRecommendedApplianceFieldsFragment[],
  savedApplianceId: string | null | undefined,
  detail?: string
): SuggestedEquipmentItem => {
  if (savedApplianceId === null) return { label, name: 'Not included' };
  const selection = savedApplianceId
    ? appliances.find(({ appliance }) => appliance.id === savedApplianceId)
    : appliances[0];

  if (selection) {
    return {
      label: savedApplianceId === undefined ? `${label} recommendation` : label,
      name: selection.appliance.productSpec.name,
      detail:
        selection.quantity != null
          ? `${selection.quantity} units · ${selection.totalAcKw ?? 'Unknown'} kW AC`
          : detail,
    };
  }

  const unavailableReason = savedApplianceId
    ? 'Saved choice is unavailable for this layout'
    : 'No matching option is available';
  return {
    label,
    name: 'Unavailable',
    detail: detail ? `${detail} · ${unavailableReason}` : unavailableReason,
  };
};

/** Saved equipment choices; legacy automatic suggestions remain labelled as such. */
const SolarDesignEquipment: FC<SolarDesignEquipmentProps> = (props) => {
  const { projectId, source, draft } = props;
  const context = useMemo(
    () => solarDesignEquipmentContext(source, draft.state),
    [source, draft.state]
  );
  const includeBattery = context?.state.includeBattery ?? false;
  const inverterMatches = useSolarDesignerInverters(
    projectId,
    context?.panelsCount ?? 0,
    context?.panelCapacityWatts ?? null,
    includeBattery,
    draft.id,
    context
      ? new Set(
          context.state.layout.workingPanels
            .filter(
              (panel) =>
                panel.included &&
                !context.state.layout.excludedFaceIds.includes(panel.faceId)
            )
            .map((panel) => panel.faceId)
        ).size
      : null,
    context?.state.selectedPanelApplianceId,
    {
      revision: draft.revision,
      basis: draft.basis,
      sourceAnalysisId: draft.sourceAnalysisId,
      sourceAnalysisVersion: draft.sourceAnalysisVersion,
    },
    true,
    context?.state.batteryQuantity ?? 1
  );

  if (!context) {
    return (
      <section aria-label="Equipment" className="space-y-2">
        <H4>Equipment</H4>
        <Text as="div" role="alert" level="supporting">
          Selected equipment could not be loaded.
        </Text>
      </section>
    );
  }

  if (inverterMatches.isLoading) {
    return (
      <section aria-label="Equipment" className="space-y-2">
        <H4>Equipment</H4>
        <SkeletonLoader
          className="h-12 w-full rounded-md"
          aria-label="Loading selected equipment"
        />
      </section>
    );
  }

  if (inverterMatches.isError) {
    return (
      <section aria-label="Equipment" className="space-y-2">
        <H4>Equipment</H4>
        <Text as="div" role="alert" level="supporting">
          Selected equipment could not be loaded.
        </Text>
      </section>
    );
  }

  const panelCountDetail = `${context.panelsCount} ${
    context.panelsCount === 1 ? 'panel' : 'panels'
  }`;
  const pvOptions = [
    ...inverterMatches.recommendedInverterAppliances,
    ...inverterMatches.excludedInverterAppliances,
  ];
  const inverterApplianceId = selectedSolarInverterApplianceId(
    context.state.inverter
  );
  const combined =
    includeBattery &&
    inverterApplianceId != null &&
    inverterApplianceId === context.state.selectedBatteryApplianceId &&
    pvOptions.some(
      (item) =>
        item.appliance.id === inverterApplianceId &&
        item.appliance.productSpec.pvConversion != null
    );
  const items = [
    resolvedEquipmentItem(
      'Panels',
      context.panelAppliances,
      context.state.selectedPanelApplianceId ?? undefined,
      panelCountDetail
    ),
    resolvedEquipmentItem(
      combined ? 'PV and storage' : 'Inverter',
      inverterApplianceId
        ? pvOptions
        : inverterMatches.recommendedInverterAppliances.filter(
            (item) => item.appliance.productSpec.category === 'INVERTER'
          ),
      context.state.inverter.mode === 'automatic'
        ? undefined
        : inverterApplianceId
    ),
  ];
  if (includeBattery && !combined) {
    items.push(
      resolvedEquipmentItem(
        'Battery',
        source.recommendedBatteryAppliances,
        context.legacyBatterySelection
          ? undefined
          : context.state.selectedBatteryApplianceId,
        `${context.state.batteryQuantity ?? 1} ${context.state.batteryQuantity === 1 || context.state.batteryQuantity == null ? 'unit' : 'units'}`
      )
    );
  }

  return <SuggestedEquipment title="Equipment" items={items} />;
};

export default SolarDesignEquipment;
